import { DataGridPremiumProps } from '@mui/x-data-grid-premium';
import { type MouseEvent } from 'react';

const actionColorRgb = '0, 81, 215'; // #0051D7
const focusBackgroundColor = `rgba(${actionColorRgb}, 0.07)`; // 7% opacity
const focusVisibleBackgroundColor = `rgba(${actionColorRgb}, 0.3)`; // 30% opacity
const hoverBackgroundColor = `rgba(${actionColorRgb}, 0.04)`; // 4% opacity
// Opaque equivalent of hoverBackgroundColor blended over white, so pinned
// cells keep the hover tint without letting scrolled cells show through.
export const pinnedHoverBackgroundColor = 'rgb(245, 248, 253)';
const outlinedBorderColor = `rgba(${actionColorRgb}, 0.5)`; // 50% opacity
const selectedBackgroundColor = `rgba(${actionColorRgb}, 0.08)`; // 8% opacity
const CELL_TITLE_DATA_ATTRIBUTE = 'cellTitle';

function isOverflown(element: HTMLElement) {
  return (
    element.scrollHeight > element.clientHeight ||
    element.scrollWidth > element.clientWidth
  );
}

function showCellTitleOnlyWhenOverflown(
  event: MouseEvent<HTMLDivElement, globalThis.MouseEvent>,
) {
  const cell = event.currentTarget;
  const title = cell.getAttribute('title');

  if (title !== null) {
    cell.dataset[CELL_TITLE_DATA_ATTRIBUTE] = title;
  } else if (cell.textContent !== cell.dataset[CELL_TITLE_DATA_ATTRIBUTE]) {
    delete cell.dataset[CELL_TITLE_DATA_ATTRIBUTE];
  }

  const storedTitle = cell.dataset[CELL_TITLE_DATA_ATTRIBUTE];
  if (!storedTitle) {
    return;
  }

  if (isOverflown(cell)) {
    cell.setAttribute('title', storedTitle);
  } else {
    cell.removeAttribute('title');
  }
}

/**
 * Styling for the DataGrid component.
 *
 * @see https://mui.com/x/react-data-grid/data-grid-pro/#styling
 */
export const sx: DataGridPremiumProps['sx'] = {
  '& .child': {
    backgroundColor: 'inherit',
  },
  '& .child .MuiDataGrid-cell, & .group .MuiDataGrid-cell': {
    border: 'none',
    borderColor: 'transparent',
  },
  '& .error:not(.Mui-selected):not(:hover)': {
    backgroundColor: '#fff1f2',
  },
  '& .group': {
    backgroundColor: '#f5f5f5',
    fontWeight: 500,
  },
  '& .MuiDataGrid-cell--withLeftBorder, & .MuiDataGrid-columnHeader--withLeftBorder':
    {
      borderLeft: 'none',
    },
  '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
    outline: 'none',
  },
  '& .MuiDataGrid-cellCheckbox .MuiCheckbox-root:not(.Mui-checked)': {
    opacity: 0,
  },
  '& .MuiDataGrid-cellEmpty': {
    border: 'none',
    borderBottom: 'none',
    borderLeft: 'none',
    borderRight: 'none',
    borderTop: 'none',
  },
  '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within':
    {
      outline: `solid ${focusBackgroundColor} 1px`,
      outlineOffset: -1,
    },
  '& .MuiDataGrid-columnHeader:focus-visible': {
    outline: `solid ${focusVisibleBackgroundColor} 1px`,
    outlineOffset: -1,
  },
  // Header separators show only while the header row is hovered; the pointer can
  // leave the row mid-drag, so a resizing separator stays visible.
  '& .MuiDataGrid-columnHeaders:not(:hover) .MuiDataGrid-columnSeparator:not(.MuiDataGrid-columnSeparator--resizing)':
    {
      color: 'transparent',
    },
  '& .MuiDataGrid-filler > div': {
    border: 'none',
    borderColor: 'transparent',
  },
  '& .MuiDataGrid-row.Mui-selected': {
    backgroundColor: selectedBackgroundColor,
  },
  '& .MuiDataGrid-row.Mui-selected:hover': {
    backgroundColor: selectedBackgroundColor,
  },
  '& .MuiDataGrid-row.MuiDataGrid-row:focus-within:not(.Mui-selected):not(.child):not(.group)':
    {
      backgroundColor: focusBackgroundColor,
    },
  '& .MuiDataGrid-row.MuiDataGrid-row:has(.MuiDataGrid-cell:focus-visible):not(.Mui-selected):not(.child):not(.group)':
    {
      outline: `solid ${focusVisibleBackgroundColor} 1px`,
      outlineOffset: -1,
    },
  '& .MuiDataGrid-row:hover': {
    backgroundColor: hoverBackgroundColor,
  },
  '& .MuiDataGrid-row:hover .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-row:hover .MuiDataGrid-cell--pinnedRight':
    {
      backgroundColor: pinnedHoverBackgroundColor,
    },
  '& .MuiDataGrid-row:hover .MuiDataGrid-cellCheckbox .MuiCheckbox-root, & .MuiDataGrid-row:focus-within .MuiDataGrid-cellCheckbox .MuiCheckbox-root':
    {
      opacity: 1,
    },
  '& .MuiDataGrid-virtualScroller': {
    overflow: 'auto',
  },
  '& .MuiDataGrid-withBorderColor': {
    borderColor: outlinedBorderColor,
  },
  '& .MuiDataGrid-withBorderColor.MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-withBorderColor.MuiDataGrid-columnHeader--pinnedLeft, & .MuiDataGrid-withBorderColor.MuiDataGrid-cell--pinnedRight, & .MuiDataGrid-withBorderColor.MuiDataGrid-columnHeader--pinnedRight, & .MuiDataGrid-withBorderColor.MuiDataGrid-cell--withLeftBorder, & .MuiDataGrid-withBorderColor.MuiDataGrid-columnHeader--withLeftBorder, & .MuiDataGrid-withBorderColor.MuiDataGrid-cell--withRightBorder, & .MuiDataGrid-withBorderColor.MuiDataGrid-columnHeader--withRightBorder':
    {
      borderColor: 'var(--DataGrid-rowBorderColor)',
    },
};

export const slotProps: DataGridPremiumProps['slotProps'] = {
  cell: {
    className: 'text-tiga-text-default border-t',
    onMouseOver: showCellTitleOnlyWhenOverflown,
    style: {
      fontSize: 'var(--text-sm)',
    },
  },
  columnHeaders: {
    className:
      'font-semibold text-sm [&_.MuiDataGrid-columnHeader]:bg-white [&>div]:first-of-type:contain-paint [&_.MuiDataGrid-filler]:bg-white [&_.MuiDataGrid-scrollbarFiller--header]:bg-white ',
  },
  footer: {
    className: 'border-t-0',
  },
  root: {
    className: 'rounded-none border-0',
  },
  row: {
    className:
      '[&:not(:hover):not(.Mui-selected):not(.error):not(.group)]:bg-white',
  },
};
